import { DateTime } from "luxon";
import type { RecurringCandidateRow } from "../../../../services/backend.service";
import { formatClock } from "./recurringDisplay";

/**
 * The days a proposed pattern would put someone on, computed in the browser.
 *
 * This mirrors `FittedPattern.generated_days` in `backend/services/shcedule_loader/recurring_fit.py`
 * line for line, and it is the one place in this feature where logic is duplicated across the
 * language boundary. That is a deliberate trade for a PREVIEW: the alternative is a round trip per
 * employee to render a calendar the reviewer opens and closes in a second. The risk is real though —
 * if the backend's generation changes, this silently disagrees — so the two rules it depends on are
 * spelled out rather than inferred:
 *
 *  - A **rotation** counts days from the anchor DATE: `(day - anchor) % (work + rest) < work`, and
 *    nothing before the anchor is generated.
 *  - A **cyclical** pattern counts cycle weeks from the anchor's WEEK, not the anchor day, which is
 *    why `weekStartDay` is on the row at all. A day belongs to the pattern when
 *    `(weeksSinceAnchorWeek % cycleWeeks, weekdayName(day))` is one of its slots.
 *
 * Nothing here decides anything — approving still sends candidate ids, and the backend regenerates
 * the real rows from its own pattern. A wrong day here is a wrong picture, never a wrong template.
 */

/**
 * Cycle length in weeks. Mirrors `cycle_weeks_of`: the named kinds are fixed, and a custom cycle
 * carries its own length on the row, so the row wins whenever it has one.
 */
const NAMED_CYCLE_WEEKS: Partial<Record<NonNullable<RecurringCandidateRow["patternKind"]>, number>> = {
	everyWeek: 1,
	twoWeekly: 2,
	fourWeekly: 4,
};

const DAYS_PER_WEEK = 7;

/** Lowercase weekday name — the spelling `cycleSlots` uses. Mirrors `weekday_name`. */
function weekdayName(day: DateTime): string {
	return day.toFormat("cccc").toLowerCase();
}

/**
 * Start of the week containing `day`, for a week beginning on `weekStartDay` (0 = Monday, matching
 * Python's `date.weekday()`). Mirrors `week_start_for`.
 */
function weekStart(day: DateTime, weekStartDay: number): DateTime {
	// Luxon's `weekday` is 1=Monday..7=Sunday; the backend's is 0=Monday..6=Sunday.
	const offset = (day.weekday - 1 - weekStartDay + DAYS_PER_WEEK) % DAYS_PER_WEEK;
	return day.minus({ days: offset }).startOf("day");
}

const toDateTime = (value: RecurringCandidateRow["anchor"]): DateTime | null => {
	if (!value) return null;
	const parsed = typeof value === "string" ? DateTime.fromISO(value) : value;
	return parsed.isValid ? parsed.startOf("day") : null;
};

/** ISO `yyyy-MM-dd` keys for every day the pattern lands on inside `[from, to]`. */
export function generatedShiftDays(row: RecurringCandidateRow, from: DateTime, to: DateTime): Set<string> {
	const days = new Set<string>();
	if (!row.patternKind) return days;

	const anchor = toDateTime(row.anchor);
	const start = from.startOf("day");
	const end = to.startOf("day");

	if (row.patternKind === "rotation") {
		const work = row.workDays ?? 0;
		const cycle = work + (row.restDays ?? 0);
		if (!anchor || cycle <= 0 || work <= 0) return days;
		for (let day = start; day <= end; day = day.plus({ days: 1 })) {
			if (day < anchor) continue;
			const sinceAnchor = Math.round(day.diff(anchor, "days").days);
			if (sinceAnchor % cycle < work) days.add(day.toISODate() ?? "");
		}
		return days;
	}

	const cycleWeeks = row.cycleWeeks ?? NAMED_CYCLE_WEEKS[row.patternKind];
	if (!cycleWeeks || !anchor || row.weekStartDay === null) return days;
	const anchorWeek = weekStart(anchor, row.weekStartDay);
	const slots = new Set(row.cycleSlots.map(([week, weekday]) => `${week}|${weekday}`));

	for (let day = start; day <= end; day = day.plus({ days: 1 })) {
		const weeksSince = Math.round(weekStart(day, row.weekStartDay).diff(anchorWeek, "days").days) / DAYS_PER_WEEK;
		// A day before the anchor week gives a negative index; JS `%` keeps the sign, so normalize it
		// the way Python's `%` already does, or weeks before the anchor never match their own slot.
		const cycleIndex = ((weeksSince % cycleWeeks) + cycleWeeks) % cycleWeeks;
		if (slots.has(`${cycleIndex}|${weekdayName(day)}`)) days.add(day.toISODate() ?? "");
	}
	return days;
}

/** The shift times a generated day carries, e.g. "07:00-19:00". A day inherits the row's times. */
export function shiftTimeLabels(row: RecurringCandidateRow): string[] {
	return row.shiftTimes.map((slot) => `${formatClock(slot.start)}-${formatClock(slot.end)}`);
}
